<template>
  <div>
    <RegistrationLayoutTitles
      :title="$t('registration.member_info.title')"
      :subTitle="$t('registration.member_info.description')"
    />
    <div class="md:w-458 md:mx-auto">
      <Form :validator="$v.model" :error="error">
        <div class="relative flex items-center">
          <FormInput
            v-model="model.memberNumber"
            class="w-full mt-16"
            :label="$t('registration.member_info.member_number')"
            name="memberNumber"
            :placeholder="
              $t('registration.member_info.member_number_place_holder')
            "
            :helperText="$t('registration.member_info.member_number_helper')"
          />
          <div class="absolute top-0 right-0 pt-5 mr-10 mt-50">
            <v-popover>
              <button
                class="text-center text-white rounded-full bg-grey-500 w-25 h-25 focus:shadow-outline"
                aria-controls="member-number-tooltip"
                :aria-label="
                  $t('registration.member_info.member_number_helper_btn')
                "
              >
                ?
              </button>
              <div
                id="member-number-tooltip"
                slot="popover"
                role="tooltip"
                class="max-w-xs"
              >
                <div
                  v-for="(card, idx) in insuranceCards"
                  :key="idx"
                  :class="{ 'mb-20': idx !== insuranceCards.length - 1 }"
                >
                  <p v-if="card.name" class="mb-4 font-bold">
                    {{
                      $t('registration.member_info.member_card_members', [
                        card.name,
                      ])
                    }}
                  </p>
                  <img
                    class="w-250"
                    :alt="$t('registration.member_info.member_card_alt_text')"
                    :src="card.file"
                  />
                </div>
              </div>
            </v-popover>
          </div>
        </div>
        <p v-if="memberIdPrompt" class="pt-4 pl-2 text-grey-500">
          {{ memberIdPrompt }}
        </p>
      </Form>
      <div class="px-40">
        <HMButton
          id="nextButton"
          rounded="full"
          class="w-full tracking-wide"
          fontWeight="semibold"
          :class="{ 'mt-10': formHasError, 'mt-40': !formHasError }"
          :busy="isFetching"
          @click="handleNext"
          >{{ $t('common.next_btn') }}
        </HMButton>
      </div>
    </div>
    <Modal :active="showLanguageChangeModal" @close="cancelLanguageChange">
      <LanguageChangeModal
        @confirm="confirmLanguageChange"
        @cancel="cancelLanguageChange"
      />
    </Modal>
  </div>
</template>

<script>
import { Form, FormInput } from '../../common/forms'
import RegistrationLayoutTitles from '@components/layouts/RegistrationLayoutTitles'
import { required } from 'vuelidate/lib/validators'
import { mapActions, mapGetters } from 'vuex'
import _get from 'lodash/get'
import _map from 'lodash/map'
import { FEATURES } from '@platform-shared/constants'
import Modal from '@components/common/Modal'
import LanguageChangeModal from './LanguageChangeModal'

export default {
  components: {
    Form,
    FormInput,
    RegistrationLayoutTitles,
    Modal,
    LanguageChangeModal,
  },
  props: {
    initialEligibilityData: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      showLanguageChangeModal: false,
      model: {
        memberNumber: '',
      },
      error: null,
      eligibilityData: null,
    }
  },
  validations: {
    model: {
      memberNumber: {
        required,
      },
    },
  },
  computed: {
    ...mapGetters('client', [
      'lingo',
      'memberCards',
      'hasFeature',
      'languages',
    ]),
    ...mapGetters('registration', [
      'databasePreferredLanguageCd',
      'isFetching',
    ]),
    ...mapGetters('member', ['preAuthPreferredLanguageCd']),
    formHasError() {
      return !!this.error
    },
    insuranceCards() {
      return _map(this.memberCards, (card) => ({
        ...card,
        file: `${window.HM_CONFIG.S3_ROOT}/${card.file}`,
      }))
    },
    memberIdPrompt() {
      return _get(this.lingo, 'memberIdPrompt', '')
    },

    hasGuardianshipFeature() {
      return this.hasFeature(FEATURES.GUARDIANSHIP)
    },
  },
  methods: {
    ...mapActions('member', ['setPreAuthPreferredLanguage']),
    ...mapActions('registration', [
      'saveMemberInfo',
      'saveAccountInfo',
      'saveGuardianInfo',
      'checkEligibility',
    ]),

    openLanguageChangeModal() {
      this.showLanguageChangeModal = true
    },
    closeLanguageChangeModal() {
      this.showLanguageChangeModal = false
    },
    confirmLanguageChange() {
      this.setPreAuthPreferredLanguage(this.databasePreferredLanguageCd)
      this.$root.$i18n.locale = this.databasePreferredLanguageCd
      this.cancelLanguageChange()
    },
    cancelLanguageChange() {
      this.closeLanguageChangeModal()
      this.$emit('member-found', this.eligibilityData)
    },
    handleNext() {
      this.error = null
      this.eligibilityData = null
      this.$v.$touch()

      if (this.$v.$invalid) return

      this.eligibilityData = {
        ...this.initialEligibilityData,
        ...this.model,
      }

      this.checkEligibility(this.eligibilityData)
        .then((data) => {
          this.saveAccountInfo(data)
          this.saveMemberInfo({ memberNumber: this.model.memberNumber })

          if (this.initialEligibilityData.isGuardian) {
            this.saveGuardianInfo(this.initialEligibilityData)
          }

          if (
            this.preAuthPreferredLanguageCd === 'EN' &&
            this.languages.some(
              (lang) => lang.code === this.databasePreferredLanguageCd
            ) &&
            this.preAuthPreferredLanguageCd !== this.databasePreferredLanguageCd
          ) {
            this.openLanguageChangeModal()
          } else {
            this.$emit('member-found', this.eligibilityData)
          }
        })
        .catch(({ message }) => {
          this.error = message
        })
    },
  },
}
</script>
